// Modal "Nuevo ticket" compartido entre el panel de la ficha del lead
// (LeadTickets, Fase 2) y el tab global de Tickets (Fase 4). Parametrizado con
// un `leadUlid` opcional: cuando se abre desde la ficha del lead se pre-vincula
// el ticket al lead; desde el tab global se crea sin lead asociado.
//
// El CRM usa el MISMO ULID del lead que Operations como PK (leadId acepta hex 32
// o Crockford 26), así que pasamos `leadUlid` tal cual como `leadId`.

import { useEffect, useState } from "react";
import { CrmHttpError, describeCrmError } from "../../api/crm";
import {
  addTicketComment,
  createTicket,
  listTicketPipelines,
  listTicketStages,
} from "../../api/crmTickets";
import { AttachmentPickerUI, useAttachmentPicker } from "./attachmentPicker";
import {
  inlineImageToken,
  makePastedImageFilename,
} from "../../api/crmInlineImages";
import {
  CreateTicketBody,
  TicketPipeline,
  TicketStage,
} from "../../api/crmTypes";
import {
  TICKET_INCIDENT_TYPES,
  ticketPipelineHasIncidentType,
  typificationsForPipeline,
} from "../../api/crmTicketTypification";
import { TypificationSelect } from "./TypificationSelect";
import { useAlert, useConfirm } from "../Dialog";
import {
  ErrorBox,
  Field,
  GhostButton,
  ModalShell,
  PrimaryButton,
  Select,
  TextArea,
  TextInput,
} from "../FormControls";

export default function CreateTicketModal({
  leadUlid,
  pipelines: presetPipelines,
  onClose,
  onCreated,
}: {
  /** ULID del lead a vincular; omitir para crear un ticket sin lead. */
  leadUlid?: string;
  /**
   * Pipelines ya cargados (sin archivar) por el llamador. Si vienen, el modal no
   * vuelve a fetchear pipelines; si se omite, los carga él mismo (LeadTickets).
   */
  pipelines?: TicketPipeline[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const alert = useAlert();
  const confirm = useConfirm();

  const [pipelines, setPipelines] = useState<TicketPipeline[] | null>(
    presetPipelines ?? null,
  );
  const [stages, setStages] = useState<TicketStage[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);

  const [pipelineId, setPipelineId] = useState("");
  const [stageId, setStageId] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [incidentType, setIncidentType] = useState("");
  const [typification, setTypification] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const picker = useAttachmentPicker();

  // Carga de pipelines al abrir, salvo que el llamador ya los haya precargado.
  useEffect(() => {
    if (presetPipelines) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await listTicketPipelines();
        if (cancelled) return;
        const active = res.filter((p) => !p.archived);
        setPipelines(active);
      } catch {
        if (!cancelled) setError("No se pudieron cargar los pipelines");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [presetPipelines]);

  // Carga de stages dependiente del pipeline seleccionado.
  useEffect(() => {
    // Al cambiar de pipeline, la Opción 1 (global) se conserva pero la Opción 2
    // (escopada) deja de ser válida: se limpia siempre.
    setTypification("");
    if (!pipelineId) {
      setStages([]);
      setStageId("");
      return;
    }
    let cancelled = false;
    setLoadingStages(true);
    (async () => {
      try {
        const res = await listTicketStages(pipelineId);
        if (cancelled) return;
        const active = res.filter((s) => !s.archived);
        setStages(active);
        // Por defecto la primera stage abierta (como hace el CRM al omitir stageId).
        const firstOpen = active.find((s) => s.ticketState === "open");
        setStageId(firstOpen?.id ?? active[0]?.id ?? "");
      } catch {
        if (!cancelled) setError("No se pudieron cargar las etapas del pipeline");
      } finally {
        if (!cancelled) setLoadingStages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pipelineId]);

  const submit = async () => {
    if (submitting) return;
    if (!pipelineId) {
      setError("Selecciona un pipeline");
      return;
    }
    if (!subject.trim()) {
      setError("El título es obligatorio");
      return;
    }
    const ok = await confirm({
      title: "Crear ticket",
      message: leadUlid
        ? `Se creará el ticket "${subject.trim()}" vinculado a este lead. ¿Continuar?`
        : `Se creará el ticket "${subject.trim()}". ¿Continuar?`,
      confirmLabel: "Crear ticket",
      tone: "primary",
    });
    if (!ok) return;

    setSubmitting(true);
    setError(null);
    const body: CreateTicketBody = {
      pipelineId,
      subject: subject.trim(),
      ...(leadUlid ? { leadId: leadUlid } : {}),
      ...(stageId ? { stageId } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(incidentType ? { incidentType } : {}),
      ...(typification ? { typification } : {}),
    };
    let created;
    try {
      created = await createTicket(body);
    } catch (err) {
      const msg =
        err instanceof CrmHttpError
          ? describeCrmError(err)
          : "No se pudo crear el ticket";
      setError(msg);
      await alert({
        title: "Error al crear el ticket",
        message: msg,
        tone: "danger",
      });
      setSubmitting(false);
      return;
    }

    // El contrato POST /tickets no acepta adjuntos: si el usuario los seleccionó
    // se suben como una nota sobre el ticket ya creado. Las imágenes (pegadas o
    // adjuntadas) van con su token inline en el body de esa nota, así se ven
    // renderizadas en la primera nota tanto en Operations como en el CRM.
    // Un fallo aquí (típicamente attachments_disabled/503 mientras la infra GCS
    // no esté lista) NO revierte el ticket: lo damos por creado y avisamos.
    const attachmentBody = picker.toBodyAttachments();
    if (attachmentBody.length > 0) {
      const imageTokens = attachmentBody
        .filter((a) => a.contentType.startsWith("image/"))
        .map((a) => inlineImageToken(a.filename))
        .join("\n");
      try {
        await addTicketComment(created.id, {
          body: imageTokens,
          attachments: attachmentBody,
        });
      } catch (err) {
        setSubmitting(false);
        onCreated();
        await alert({
          title: "Ticket creado, pero sin adjuntos",
          message: `El ticket se creó correctamente, pero los adjuntos no se pudieron subir: ${
            err instanceof CrmHttpError
              ? describeCrmError(err)
              : "error de conexión con el CRM."
          }`,
          tone: "danger",
        });
        return;
      }
    }

    setSubmitting(false);
    onCreated();
  };

  const pipelineOptions = (pipelines ?? []).map((p) => ({
    value: p.id,
    label: p.label,
  }));
  const stageOptions = stages.map((s) => ({ value: s.id, label: s.label }));

  // Tipificaciones (2026-07-16): Opción 1 global si el pipeline tipifica;
  // Opción 2 escopada por pipeline (oculta si el pipeline no tiene lista).
  const showIncidentType = !!pipelineId && ticketPipelineHasIncidentType(pipelineId);
  const typificationGroups = pipelineId ? typificationsForPipeline(pipelineId) : [];

  return (
    <ModalShell
      title="Nuevo ticket"
      onClose={onClose}
      width={520}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={submitting}>
            Cancelar
          </GhostButton>
          <PrimaryButton onClick={submit} disabled={submitting}>
            {submitting ? "Creando…" : "Crear ticket"}
          </PrimaryButton>
        </>
      }
    >
      <Field label="Pipeline">
        <Select
          value={pipelineId}
          onChange={setPipelineId}
          options={pipelineOptions}
          placeholder={pipelines === null ? "Cargando…" : "Selecciona un pipeline"}
          disabled={submitting || pipelines === null}
        />
      </Field>

      <Field
        label="Etapa"
        hint={!pipelineId ? "Selecciona antes un pipeline" : undefined}
      >
        <Select
          value={stageId}
          onChange={setStageId}
          options={stageOptions}
          placeholder={loadingStages ? "Cargando…" : "Selecciona una etapa"}
          disabled={submitting || !pipelineId || loadingStages}
        />
      </Field>

      {showIncidentType && (
        <Field label="Tipo de incidencia (opcional)">
          <Select
            value={incidentType}
            onChange={setIncidentType}
            options={TICKET_INCIDENT_TYPES.map((it) => ({ value: it, label: it }))}
            placeholder="—"
            disabled={submitting}
          />
        </Field>
      )}

      {typificationGroups.length > 0 && (
        <Field label="Tipificación (opcional)">
          <TypificationSelect
            value={typification}
            onChange={setTypification}
            groups={typificationGroups}
            disabled={submitting}
          />
        </Field>
      )}

      <Field label="Título">
        <TextInput
          value={subject}
          onChange={setSubject}
          disabled={submitting}
          maxLength={500}
        />
      </Field>

      <Field
        label="Descripción (opcional)"
        hint="Puedes pegar capturas de pantalla aquí: se añaden como adjunto y se verán en la primera nota del ticket."
      >
        <TextArea
          value={description}
          onChange={setDescription}
          disabled={submitting}
          rows={3}
          onPaste={(e) => {
            // Pegar imágenes al crear: el ticket aún no existe y la descripción
            // no renderiza tokens, así que la imagen entra como ADJUNTO (chip
            // abajo) y al enviar viaja en la primera nota con su token inline.
            const images = Array.from(e.clipboardData?.items ?? [])
              .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
              .map((it) => it.getAsFile())
              .filter((f): f is File => f !== null);
            if (images.length === 0) return;
            e.preventDefault();
            void picker.addFiles(
              images.map(
                (f) =>
                  new File([f], makePastedImageFilename(f.type), {
                    type: f.type,
                  }),
              ),
            );
          }}
        />
      </Field>

      <Field label="Adjuntos (opcional)">
        <AttachmentPickerUI picker={picker} disabled={submitting} />
      </Field>

      {error && <ErrorBox>{error}</ErrorBox>}
    </ModalShell>
  );
}
